import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Download as DownloadIcon, AutoAwesome as GenerateIcon } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay, isAfter, isBefore, min } from 'date-fns';
import axios from 'axios';
import config from '../config';

/** Legacy KPI keys removed from schema; never show in table or CSV even if API returns them. */
const DEPRECATED_AI_REPORT_KEYS = new Set(['patientYear', 'aiEndToEndRequestCompleted']);

const truncate = (v, max = 80) => {
  if (v === undefined || v === null) return '';
  const s = String(v);
  return s.length <= max ? s : `${s.slice(0, max)}…`;
};

/** Prefer CallReport-aligned `datetime` (same value as legacy DateTime column); fall back to extracted field */
function getDateTimeOfCallDisplay(r) {
  if (r.datetime) {
    try {
      return format(new Date(r.datetime), 'yyyy-MM-dd HH:mm');
    } catch {
      return '';
    }
  }
  if (r.dateTimeOfCall == null || r.dateTimeOfCall === '') return '';
  return String(r.dateTimeOfCall);
}

/** Validates UI date range against server rules (past / today allowed; arbitrary span including prior months). */
function aiReportRangeError(start, end) {
  if (!start || !end) return null;
  const s = startOfDay(start);
  const e = startOfDay(end);
  const today = startOfDay(new Date());
  if (isAfter(s, today)) return 'Start date cannot be in the future';
  if (isAfter(e, today)) return 'End date cannot be in the future';
  if (isAfter(s, e)) return 'Start date must be on or before end date';
  return null;
}

const AIRuleBasedReports = () => {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return startOfMonth(now);
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return min([endOfMonth(now), now]);
  });
  const [agents, setAgents] = useState([]);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [replaceExisting, setReplaceExisting] = useState(true);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    setUser(currentUser);
    if (currentUser && currentUser.role === 'admin') {
      loadAgents();
    } else {
      window.location.href = '/login';
    }
  }, []);

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/ai-call-reports/agents`);
      setAgents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error loading agents:', err);
      setError('Failed to load agents');
    }
  };

  const loadReports = useCallback(async () => {
    if (!selectedAgent || !startDate || !endDate) {
      setRows([]);
      return;
    }
    const rangeErr = aiReportRangeError(startDate, endDate);
    if (rangeErr) {
      setRows([]);
      setTotal(0);
      setColumns([]);
      setError(rangeErr);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        agentCustomId: selectedAgent,
        startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
        endDate: format(endOfDay(endDate), 'yyyy-MM-dd'),
        page: String(page + 1),
        limit: String(rowsPerPage)
      });
      const response = await axios.get(`${config.apiBaseUrl}/api/ai-call-reports/reports?${params}`);
      if (response.data.success) {
        const rawCols = response.data.columns || [];
        const cols = rawCols.filter(
          (c) => !DEPRECATED_AI_REPORT_KEYS.has(c.key) && c.label !== 'Patient Year'
        );
        const data = (response.data.data || []).map((row) => {
          const copy = { ...row };
          DEPRECATED_AI_REPORT_KEYS.forEach((k) => {
            delete copy[k];
          });
          return copy;
        });
        setRows(data);
        setColumns(cols);
        setTotal(response.data.pagination?.total || 0);
      } else {
        setError(response.data.message || 'Failed to load reports');
      }
    } catch (err) {
      console.error('Error loading AI reports:', err);
      setError(err.response?.data?.message || 'Failed to load AI reports');
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, startDate, endDate, page, rowsPerPage]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleStartDateChange = (newDate) => {
    if (!newDate) return;
    const dayStart = startOfDay(newDate);
    const todayStart = startOfDay(new Date());
    if (isAfter(dayStart, todayStart)) {
      setError('Start date cannot be in the future');
      return;
    }
    const defaultEnd = min([endOfMonth(newDate), new Date()]);
    setStartDate(newDate);
    setEndDate(defaultEnd);
    setError('');
    setPage(0);
  };

  const handleEndDateChange = (newDate) => {
    if (!newDate) return;
    const msg = aiReportRangeError(startDate, newDate);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    setEndDate(newDate);
    setPage(0);
  };

  const handleAgentChange = (event) => {
    setSelectedAgent(event.target.value);
    setPage(0);
  };

  const handleGenerate = async () => {
    if (!selectedAgent) {
      setError('Select an agent');
      return;
    }
    const rangeErr = aiReportRangeError(startDate, endDate);
    if (rangeErr) {
      setError(rangeErr);
      return;
    }
    const agent = agents.find((a) => a.customAgentId === selectedAgent);
    if (agent && agent.aiReporting === false) {
      setError('Enable AI reporting on this agent before generating.');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      setInfo('');
      await axios.post(`${config.apiBaseUrl}/api/ai-call-reports/generate`, {
        agentCustomId: selectedAgent,
        startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
        endDate: format(endOfDay(endDate), 'yyyy-MM-dd'),
        replaceExisting
      });
      setInfo(
        'Generation has been scheduled in the background. Refresh in a few minutes; ensure GEMINI_ENABLED=true for LLM extraction.'
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to schedule generation');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCsv = () => {
    if (rows.length === 0) {
      setError('No data to export');
      return;
    }
    const agent = agents.find((a) => a.customAgentId === selectedAgent);
    const agentName = agent ? agent.name : selectedAgent;

    const ftCol = columns.find((c) => c.key === 'fullTranscript');
    const colsLeading = columns.filter((c) => c.key !== 'fullTranscript');

    const keys = [
      ...colsLeading.map((c) => c.key),
      'tokens',
      'costUSD',
      'generationCount',
      'generationError',
      ...(ftCol ? ['fullTranscript'] : [])
    ];
    const headers = [
      ...colsLeading.map((c) => c.label),
      'Tokens (total, cumulative)',
      'Cost (USD est., cumulative)',
      'LLM generation count',
      'Generation error',
      ...(ftCol ? [ftCol.label] : [])
    ];

    const lines = rows.map((r) =>
      keys
        .map((k) => {
          if (k === 'dateTimeOfCall') {
            const v = getDateTimeOfCallDisplay(r).replace(/"/g, '""');
            return `"${v}"`;
          }
          if (k === 'tokens') return `"${r.tokenUsage?.totalTokenCount ?? ''}"`;
          if (k === 'costUSD') return `"${r.tokenCost?.estimatedAmount ?? ''}"`;
          if (k === 'generationCount') return `"${r.generationCount ?? ''}"`;
          if (k === 'generationError') return `"${(r.generationError || '').replace(/"/g, '""')}"`;
          if (k === 'fullTranscript') {
            const raw = String(r[k] ?? '').replace(/"/g, '""');
            const capped = raw.length > 500000 ? `${raw.slice(0, 500000)}…` : raw;
            return `"${capped}"`;
          }
          const cell = r[k];
          const s = cell === undefined || cell === null ? '' : String(cell).replace(/"/g, '""');
          const capped = s.length > 2000 ? `${s.slice(0, 2000)}…` : s;
          return `"${capped}"`;
        })
        .join(',')
    );

    const csv = [headers.map((h) => `"${h}"`).join(','), ...lines].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ai-rule-reports-${agentName}-${format(startDate, 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (user && user.role !== 'admin') {
    return null;
  }

  const fullTranscriptColumn = columns.find((c) => c.key === 'fullTranscript');
  const columnsLeading = columns.filter((c) => c.key !== 'fullTranscript');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            AI Rule Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Row-level KPI extractions persisted in <code>AICallReport</code>. Choose agent and date range (any past span
            up to today, including prior months). Generate queues <code>CallReport</code> rows in that window, then refresh
            and export CSV.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
              {info}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Agent</InputLabel>
                <Select value={selectedAgent} label="Agent" onChange={handleAgentChange}>
                  {agents.map((agent) => (
                    <MenuItem key={agent.customAgentId} value={agent.customAgentId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {agent.name}
                        {agent.aiReporting ? (
                          <Chip size="small" label="AI report" color="primary" variant="outlined" />
                        ) : (
                          <Chip size="small" label="off" variant="outlined" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={new Date()}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                maxDate={new Date()}
                shouldDisableDate={(date) =>
                  isBefore(startOfDay(date), startOfDay(startDate)) ||
                  isAfter(startOfDay(date), startOfDay(new Date()))
                }
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                  />
                }
                label="Replace existing AI rows in range"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
              onClick={handleGenerate}
              disabled={generating || !selectedAgent}
            >
              Generate / refresh AI reports (async)
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsv} disabled={rows.length === 0}>
              Export CSV (current page)
            </Button>
            <Button
              variant="text"
              onClick={() => {
                loadReports();
              }}
              disabled={loading}
            >
              Refresh table
            </Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && rows.length > 0 && (
            <>
              <TableContainer sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columnsLeading.map((c) => (
                        <TableCell key={c.key} sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                          {c.label}
                        </TableCell>
                      ))}
                      <TableCell>Tokens (Σ)</TableCell>
                      <TableCell>Cost $ (Σ)</TableCell>
                      <TableCell>LLM runs</TableCell>
                      {fullTranscriptColumn ? (
                        <TableCell sx={{ minWidth: 280 }}>{fullTranscriptColumn.label}</TableCell>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r._id}>
                        {columnsLeading.map((c) => (
                          <TableCell key={c.key} sx={{ maxWidth: 220 }}>
                            {truncate(
                              c.key === 'dateTimeOfCall' ? getDateTimeOfCallDisplay(r) : r[c.key],
                              c.key === 'dateTimeOfCall' ? 80 : 60
                            )}
                          </TableCell>
                        ))}
                        <TableCell>{r.tokenUsage?.totalTokenCount ?? ''}</TableCell>
                        <TableCell>{r.tokenCost?.estimatedAmount ?? ''}</TableCell>
                        <TableCell>{r.generationCount ?? ''}</TableCell>
                        {fullTranscriptColumn ? (
                          <TableCell
                            sx={{
                              maxWidth: 560,
                              maxHeight: 280,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              verticalAlign: 'top'
                            }}
                          >
                            {r.fullTranscript ?? ''}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 40, 100]}
              />
            </>
          )}

          {!loading && selectedAgent && rows.length === 0 && (
            <Alert severity="info">
              No AI report rows yet for this filter. Click &quot;Generate / refresh&quot; to build records from{' '}
              <code>CallReport</code> (requires Gemini enabled on the server and AI reporting enabled on the agent).
            </Alert>
          )}

          {!selectedAgent && (
            <Alert severity="info">Select an agent to view AI rule reports.</Alert>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default AIRuleBasedReports;
